import React, {useState} from "react";
import {Autocomplete, AutocompleteRenderInputParams, CircularProgress} from "@mui/material";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {IconChevronUp} from "@bounce/shared/ui/assets";
import {TextField} from "../../../../ui/components";
import {autocompleteChevronSx} from "../../../../ui/components/inputStyles";
import {BaseQueryOptions, BaseUseQueryResult} from "@bounce/shared/requests";
import {useDebounce} from "../../../../hooks/useDebounce";
import {BaseValueInputProps} from "./types";

const MIN_SEARCH_LENGTH = 2;

type SearchVariables = {q: string};

type Props = BaseValueInputProps & {
  useSearch: (
    variables: SearchVariables,
    options?: BaseQueryOptions<string[], SearchVariables>
  ) => BaseUseQueryResult<string[]>;
  pluralNoun: string;
};

const SearchValueInput: React.FC<Props> = ({value, valueError, onChange, showLabel = true, useSearch, pluralNoun}) => {
  const [input, setInput] = useState("");
  const query = useDebounce(input);
  const {data: results = [], isFetching} = useSearch({q: query}, {enabled: query.length >= MIN_SEARCH_LENGTH});

  const selected = typeof value === "string" && value ? value : null;
  const options = selected ? Array.from(new Set([selected, ...results])) : results;

  return (
    <Box sx={{flex: 2}}>
      {showLabel && (
        <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
          Value
        </Text>
      )}
      <Autocomplete<string, false, false, false>
        size="small"
        popupIcon={<IconChevronUp style={{width: 20, height: 20}} />}
        sx={autocompleteChevronSx}
        options={options}
        filterOptions={(x) => x}
        value={selected}
        onChange={(_, next) => onChange(next ?? "")}
        onInputChange={(_, next, reason) => reason === "input" && setInput(next)}
        loading={isFetching}
        noOptionsText={
          input.trim().length < MIN_SEARCH_LENGTH
            ? `Type at least ${MIN_SEARCH_LENGTH} characters`
            : `No ${pluralNoun} found`
        }
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            placeholder={`Search ${pluralNoun}…`}
            error={!!valueError}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default SearchValueInput;
